import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Chip,
  IconButton,
  Divider
} from '@mui/material';
import { Print, Close } from '@mui/icons-material';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';

const PrintAmortizationDialog = ({ open, onClose, amortizacion, prestamo, moneda }) => {
  const handlePrint = () => {
    const printContent = document.getElementById('amortization-print-content');
    const originalContent = document.body.innerHTML;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Get the content to print
    const content = printContent.innerHTML;
    
    // Create the print document
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Tabla de Amortización - ${prestamo.loan_id || 'Préstamo'}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #55b4ac;
              padding-bottom: 20px;
            }
            .loan-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: center;
            }
            th {
              background-color: #55b4ac;
              color: white;
              font-weight: bold;
            }
            .status-paid {
              background-color: #4caf50;
              color: white;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 12px;
            }
            .status-pending {
              background-color: #ff9800;
              color: white;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 12px;
            }
            .status-cancelled {
              background-color: #f44336;
              color: white;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 12px;
            }
            .summary {
              margin-top: 30px;
              padding: 15px;
              background-color: #f5f5f5;
              border-radius: 5px;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Tabla de Amortización</h1>
            <p>Préstamo: ${prestamo.loan_id || 'N/A'}</p>
          </div>
          
          <div class="loan-info">
            <div>
              <strong>Cliente:</strong> ${prestamo.cliente_nombre || 'N/A'}<br>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Número de Cuota</th>
                <th>Fecha</th>
                <th>Cuota</th>
                <th>Valor Pagado</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              ${amortizacion.map(amort => `
                <tr>
                  <td>${amort.numero_cuota}</td>
                  <td>${amort.fecha}</td>
                  <td>${moneda?.label || '$'}${formatNumberWithCommas(amort.cuota)}</td>
                  <td>${moneda?.label || '$'}${formatNumberWithCommas(amort.valorPagado)}</td>
                  <td>
                    <span class="status-${amort.estado === 'pagado' ? 'paid' : amort.estado === 'pendiente' ? 'pending' : 'cancelled'}">
                      ${amort.estado}
                    </span>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="summary">
            <h3>Resumen</h3>
            <p><strong>Total Cuotas:</strong> ${amortizacion.length}</p>
            <p><strong>Cuotas Pagadas:</strong> ${amortizacion.filter(a => a.estado === 'pagado').length}</p>
            <p><strong>Cuotas Pendientes:</strong> ${amortizacion.filter(a => a.estado === 'pendiente').length}</p>
            <p><strong>Total Pagado:</strong> ${moneda?.label || '$'}${formatNumberWithCommas(prestamo.totalPagado || 0)}</p>
            <p><strong>Pendiente:</strong> ${moneda?.label || '$'}${formatNumberWithCommas(prestamo.capitalPendiente || 0)}</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #55b4ac 0%, #4a9b94 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 2
      }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Print sx={{ fontSize: 24 }} />
          </Box>
          <Typography variant="h6" fontWeight="bold">
            Imprimir Tabla de Amortización
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box id="amortization-print-content">
          <Box textAlign="left" mb={3} mt={2}>
            <Typography variant="subtitle1" color="text.secondary">
              Préstamo: {prestamo.loan_id || 'N/A'}
            </Typography>
          </Box>


          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#55b4ac' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Número de Cuota
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Fecha
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Cuota
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Valor Pagado
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
                    Estado
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {amortizacion.map((amort) => (
                  <TableRow key={amort.numero_cuota} hover>
                    <TableCell sx={{ textAlign: 'center' }}>{amort.numero_cuota}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{amort.fecha}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {moneda?.label || '$'}{formatNumberWithCommas(amort.cuota)}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {moneda?.label || '$'}{formatNumberWithCommas(amort.valorPagado)}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Chip
                        label={amort.estado}
                        color={amort.estado === 'pagado' ? 'success' : amort.estado === 'pendiente' ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box mt={3} p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>Resumen</Typography>
            <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Box>
                <Typography variant="body2" fontWeight="bold">Total Cuotas:</Typography>
                <Typography variant="body2">{amortizacion.length}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="bold">Cuotas Pagadas:</Typography>
                <Typography variant="body2">{amortizacion.filter(a => a.estado === 'pagado').length}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="bold">Cuotas Pendientes:</Typography>
                <Typography variant="body2">{amortizacion.filter(a => a.estado === 'pendiente').length}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="bold">Total Pagado:</Typography>
                <Typography variant="body2">{moneda?.label || '$'}{formatNumberWithCommas(prestamo.totalPagado || 0)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="bold">Pendiente:</Typography>
                <Typography variant="body2">{moneda?.label || '$'}{formatNumberWithCommas(prestamo.capitalPendiente || 0)}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ fontWeight: 'bold' }}>
          Cancelar
        </Button>
        <Button 
          onClick={handlePrint} 
          variant="contained" 
          startIcon={<Print />}
          color="primarybutton"
          sx={{fontWeight:"bold"}}
        >
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintAmortizationDialog;
